import { Box, Group, Text, TextInput, TextInputProps, createStyles } from "@mantine/core";
import React, { useId } from "react";
import { RpiAlert } from "@rpi/openapi-core";
import { Label } from "../Typography";

const useStyles = createStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.sm,
  },
  optionalText: {
    fontSize: theme.fontSizes.sm,
    color: theme.other.semanticColors.textSubtle,
  },
  input: {
    backgroundColor: theme.other.semanticColors.bg.input,
    padding: theme.spacing.sm,
    color: theme.other.semanticColors.textBold,
    fontSize: theme.fontSizes.md,
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.normal,
    lineHeight: theme.lineHeight,
    borderColor: theme.other.semanticColors.borderSubtle,
    height: "unset",
    "&::placeholder": {
      color: theme.other.semanticColors.textSubtle,
    },
    "&:hover": {
      background: theme.other.semanticColors.bg.inputHover,
    },
    "&:focus-visible": {
      borderColor: theme.other.semanticColors.borderSubtle,
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
    },
    "&:disabled": {
      backgroundColor: theme.other.semanticColors.bg.neutralDisabled,
      color: theme.other.semanticColors.text.disabled,
      opacity: "unset",
    },
    "&[data-error]": {
      borderColor: theme.other.semanticColors.alertDangerBorder,
    },
  },
  errorContainer: {
    display: "flex",
    gap: 4,
    alignItems: "start",
  },
  errorIcon: {
    lineHeight: "inherit",
  },
}));

interface RpaTextInputProps extends TextInputProps {
  optional?: boolean;
  error?: string;
}

export const RpaTextInput: React.FC<RpaTextInputProps> = ({ label, optional = false, error, icon, ...props }) => {
  const { classes, theme } = useStyles();
  const inputid = useId();

  return (
    <Box className={classes.container}>
      <Group position="apart">
        <Label as="label" htmlFor={inputid}>
          {label}
        </Label>
        {optional && <Text className={classes.optionalText}>Optional</Text>}
      </Group>
      <TextInput
        id={inputid}
        radius={theme.radius.sm}
        size="md"
        classNames={{
          input: classes.input,
        }}
        data-error={!!error || undefined}
        icon={icon}
        {...props}
      />
      {error && <RpiAlert>{error}</RpiAlert>}
    </Box>
  );
};
